import React, { useState } from 'react';
import { getApiUrl } from '../config';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  initialTargetPct: number;
  initialDeadline: string | null;
}

const GREEN = '#00E676';
const RED = '#FF5252';
const YELLOW = '#FFD600';
const CARD_BG = '#23272A';
const CARD_BORDER = '#33383B';
const MUTED = '#B0BEC5';
const PANEL_BG = '#181A1B';

const GoalModal: React.FC<Props> = ({ isOpen, onClose, onSaved, initialTargetPct, initialDeadline }) => {
  const [targetPct, setTargetPct] = useState<number>(initialTargetPct);
  const [hasDeadline, setHasDeadline] = useState<boolean>(!!initialDeadline);
  const [deadline, setDeadline] = useState<string>(initialDeadline ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const clampTarget = (n: number) => Math.min(100, Math.max(1, Math.round(n || 0)));

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    setError(null);
    const target = clampTarget(targetPct);
    const finalDeadline = hasDeadline && deadline ? deadline : null;
    try {
      setSaving(true);
      const token = localStorage.getItem('authToken');
      const res = await fetch(getApiUrl('/api/users/me/performance-goal'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ targetPct: target, deadline: finalDeadline }),
      });
      if (!res.ok) {
        let msg = 'No se pudo guardar. Probá de nuevo.';
        try {
          const data = await res.json();
          if (data?.error) msg = data.error;
        } catch {
          // noop
        }
        setError(msg);
        return;
      }
      onSaved();
      handleClose();
    } catch {
      setError('Error de conexión. Probá de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const overlay: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px',
  };
  const modal: React.CSSProperties = {
    background: CARD_BG,
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: '18px',
    padding: '20px',
    width: '100%',
    maxWidth: '440px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  };
  const label: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 600,
    color: MUTED,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '6px',
    display: 'block',
  };
  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: PANEL_BG,
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: '10px',
    color: '#FFFFFF',
    padding: '10px 12px',
    fontSize: '15px',
    boxSizing: 'border-box',
  };

  return (
    <div style={overlay} onClick={handleClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, color: '#FFFFFF', fontSize: '18px', fontWeight: 700 }}>🎯 Tu objetivo</h3>
          <button
            onClick={handleClose}
            style={{ background: 'none', border: 'none', color: MUTED, fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        {/* Objetivo % */}
        <div style={{ marginBottom: '20px' }}>
          <label style={label}>¿Qué % sin perder querés alcanzar?</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <input
              type="range"
              min={1}
              max={100}
              value={targetPct}
              onChange={(e) => setTargetPct(clampTarget(Number(e.target.value)))}
              style={{ flex: 1, accentColor: YELLOW }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <input
                type="number"
                min={1}
                max={100}
                value={targetPct}
                onChange={(e) => setTargetPct(clampTarget(Number(e.target.value)))}
                style={{ ...inputStyle, textAlign: 'center', width: '64px', flex: 'none' }}
              />
              <span style={{ color: YELLOW, fontWeight: 900, fontSize: '18px' }}>%</span>
            </div>
          </div>
        </div>

        {/* Fecha límite */}
        <div style={{ marginBottom: '20px' }}>
          <label style={label}>Fecha límite (opcional)</label>
          <input
            type="date"
            value={hasDeadline ? deadline : ''}
            disabled={!hasDeadline}
            onChange={(e) => setDeadline(e.target.value)}
            style={{ ...inputStyle, opacity: hasDeadline ? 1 : 0.4 }}
          />
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '10px',
              color: MUTED,
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={!hasDeadline}
              onChange={(e) => setHasDeadline(!e.target.checked)}
              style={{ accentColor: GREEN, width: '16px', height: '16px' }}
            />
            Sin fecha límite
          </label>
        </div>

        {error && <div style={{ color: RED, fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: '12px',
            border: 'none',
            background: YELLOW,
            color: '#181A1B',
            fontWeight: 800,
            fontSize: '16px',
            cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Guardando…' : 'Guardar objetivo'}
        </button>
      </div>
    </div>
  );
};

export default GoalModal;
